import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport surface for the admin users directory. The live BE shape drifts
 * from `Schemas['User']`; see comment on `UserDto`.
 *
 * Co-located with the feature so the DTOs and the React-Query wrappers in
 * `./api.ts` stay editable in one place.
 */

/** Live BE shape for `/users` row entries. Drifts from `Schemas['User']`. */
export interface UserDto {
  readonly uuid?: string;
  readonly name?: string;
  readonly fullName?: string;
  readonly email?: string;
  readonly status?: string;
  readonly tenantUUID?: string;
  readonly createdOn?: string;
  readonly indexNumber: number;
  /** Role names assigned to this user. Populated on list/search responses. */
  readonly roles?: string[];
}

/** Live BE shape for `GET /users` paginated response. */
export type PageUserDto = PageEnvelope<UserDto>;

/** Live BE shape for `POST /users` request body. */
export interface CreateUserRequestDto {
  readonly name: string;
  readonly email: string;
  readonly status?: string;
}

/**
 * The user-editable name parts (a separate sub-resource, not on {@code UserDto}). Snake_case matches
 * the backend {@code UserDemographicsDTO} / the update write keys. Shared by the tenant + platform
 * user pages (PTV2-1451).
 */
export interface UserDemographicsDto {
  readonly first_name?: string | null;
  readonly last_name?: string | null;
  readonly title?: string | null;
}

/** Request body for a profile (first/last/title) update — the self-editable keys. */
export interface UpdateUserProfileBody {
  readonly first_name?: string;
  readonly last_name?: string;
  readonly title?: string | null;
}

export const adminUsersEndpoints = {
  /**
   * GET /users?page=N&size=N — admin user directory.
   *
   * Live BE returns a `PageUserDTO` wrapper around `UserDTO[]`; this differs
   * from the OpenAPI contract (which still says flat `User[]`). Until the
   * contract is reconciled, the inline types above are the source of truth
   * for these three endpoints. Tracked as a follow-up against the contract.
   */
  getUsers: (
    params: { page: number; size: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageUserDto> =>
    http.get<PageUserDto>('/users', {
      ...options,
      query: { page: params.page, size: params.size },
    }),

  /**
   * GET /users/search?q=…&page=&size= — case-insensitive substring match across
   * name, full_name, email. Tenant-scoped server-side. {@code q} is required (1–200
   * chars); the BE rejects blank queries with a 400.
   */
  searchUsers: (
    params: { q: string; page?: number; size?: number },
    options?: { signal?: AbortSignal },
  ): Promise<PageUserDto> =>
    http.get<PageUserDto>('/users/search', {
      ...options,
      query: {
        q: params.q,
        ...(params.page !== undefined ? { page: params.page } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    }),

  /** GET /users/{uuid} — resolve a single user by guid (used to display `created_by` etc.). */
  getUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<UserDto> =>
    http.get<UserDto>(`/users/${encodeURIComponent(uuid)}`, options),

  /** GET /users/{uuid}/demographics — a user's editable name parts. */
  getUserDemographics: (
    uuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<UserDemographicsDto> =>
    http.get<UserDemographicsDto>(`/users/${encodeURIComponent(uuid)}/demographics`, options),

  /** GET /users/{uuid}/roles — a user's assigned role names (camelCase UserRoleAssignmentDTO). */
  getUserRoles: (
    uuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<{ userGuid?: string; roleNames?: string[] }> =>
    http.get<{ userGuid?: string; roleNames?: string[] }>(
      `/users/${encodeURIComponent(uuid)}/roles`,
      options,
    ),

  /** PUT /users/{uuid} — update a user's profile (first/last/title); self-or-admin, guarded server-side. */
  updateUserProfile: (
    uuid: string,
    body: UpdateUserProfileBody,
    options?: { signal?: AbortSignal },
  ): Promise<UserDto> => http.put<UserDto>(`/users/${encodeURIComponent(uuid)}`, body, options),

  /** POST /users — create a user (live BE shape). */
  createUser: (
    body: CreateUserRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<UserDto> => http.post<UserDto>('/users', body, options),

  /** DELETE /users/{uuid} — deactivate/delete a user. */
  deleteUser: (uuid: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/users/${encodeURIComponent(uuid)}`, options),
};
